import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Printer, FileText, Download } from 'lucide-react';
import { printHtmlSilently } from '../lib/silentPrint';
import { downloadCsv } from './DateRangeFilter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '—');
const todayStr = () => new Date().toLocaleDateString('en-CA');
const firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString('en-CA'); };

const STATUS = {
  pago: { label: 'Pago', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  parcial: { label: 'Parcial', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  pendente: { label: 'Pendente', cls: 'bg-red-100 text-red-700 border-red-200' },
};

export const CreditSalesReport = () => {
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(todayStr());
  const [statusFilter, setStatusFilter] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams({ start_date: start, end_date: end });
      if (statusFilter) params.set('status_filter', statusFilter);
      const { data: d } = await axios.get(`${API}/accounts/receivables/credit-sales/report?${params}`, { withCredentials: true });
      setData(d);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erro ao gerar relatório');
    } finally { setLoading(false); }
  };

  const handlePrint = () => {
    if (!data) return;
    const rows = data.items.map((it) => `
      <tr><td>${fmtDate(it.created_at)} #${it.sale_number || '-'}</td><td class="right">${money(it.amount)}</td></tr>
      <tr><td colspan="2">${it.customer_name} - ${STATUS[it.status]?.label || it.status}</td></tr>`).join('');
    printHtmlSilently(`<html><head><style>
      body{font-family:'Courier New',monospace;font-size:11px;color:#000}
      table{width:100%;border-collapse:collapse} td{padding:1px 0;vertical-align:top} .right{text-align:right} .center{text-align:center} .bold{font-weight:bold}
      hr{border:none;border-top:1px dashed #000;margin:4px 0}</style></head><body>
      <div class="center bold">VENDAS A PRAZO</div>
      <div class="center">${fmtDate(start + 'T12:00:00')} a ${fmtDate(end + 'T12:00:00')}</div><hr/>
      <table>${rows}</table><hr/>
      <table>
        <tr><td>Vendas no período</td><td class="right">${data.count}</td></tr>
        <tr><td class="bold">Total lançado</td><td class="right bold">${money(data.total)}</td></tr>
        <tr><td>Recebido</td><td class="right">${money(data.total_paid)}</td></tr>
        <tr><td class="bold">Pendente</td><td class="right bold">${money(data.total_pending)}</td></tr>
      </table></body></html>`);
  };

  const handleCsv = () => data && downloadCsv(`vendas_a_prazo_${start}_a_${end}.csv`, data.items, [
    { key: 'created_at', label: 'Data', format: fmtDate },
    { key: 'sale_number', label: 'Venda' },
    { key: 'customer_name', label: 'Cliente' },
    { key: 'amount', label: 'Valor (R$)', format: (v) => Number(v || 0).toFixed(2).replace('.', ',') },
    { key: 'amount_paid', label: 'Recebido (R$)', format: (v) => Number(v || 0).toFixed(2).replace('.', ',') },
    { key: 'status', label: 'Status', format: (v) => STATUS[v]?.label || v },
  ]);

  return (
    <div className="space-y-4" data-testid="credit-sales-report">
      <div className="bg-white border border-slate-200 rounded-md p-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-end overflow-hidden">
        <div><Label className="text-xs">Data inicial</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="credit-report-start" /></div>
        <div><Label className="text-xs">Data final</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="credit-report-end" /></div>
        <div>
          <Label className="text-xs">Status</Label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm" data-testid="credit-report-status">
            <option value="">Todos</option>
            <option value="pendente">Pendentes</option>
            <option value="pago">Pagos</option>
          </select>
        </div>
        <Button onClick={load} disabled={loading} className="bg-amber-600 hover:bg-amber-700 h-10" data-testid="credit-report-generate">
          <FileText size={16} className="mr-2" /> {loading ? 'Gerando...' : 'Gerar Relatório'}
        </Button>
        {data && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handlePrint} className="h-10" data-testid="credit-report-print"><Printer size={16} className="mr-1" /> Imprimir</Button>
            <Button variant="outline" onClick={handleCsv} className="h-10" data-testid="credit-report-csv"><Download size={16} className="mr-1" /> CSV</Button>
          </div>
        )}
      </div>

      {error && <p className="text-red-600 text-sm" data-testid="credit-report-error">{error}</p>}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ['Vendas a prazo', data.count, 'text-slate-900', 'credit-report-count'],
              ['Total lançado', money(data.total), 'text-slate-900', 'credit-report-total'],
              ['Recebido', money(data.total_paid), 'text-emerald-700', 'credit-report-paid'],
              ['Pendente', money(data.total_pending), 'text-red-700', 'credit-report-pending'],
            ].map(([l, v, c, t]) => (
              <div key={t} className="bg-white border border-slate-200 rounded-md p-4" data-testid={t}>
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">{l}</p>
                <p className={`text-2xl font-black mt-1 ${c}`}>{v}</p>
              </div>
            ))}
          </div>

          <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
            <table className="w-full text-sm" data-testid="credit-report-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                <tr><th className="p-3 text-left">Data</th><th className="p-3 text-left">Venda</th><th className="p-3 text-left">Cliente</th><th className="p-3 text-right">Valor</th><th className="p-3 text-right">Recebido</th><th className="p-3 text-center">Status</th></tr>
              </thead>
              <tbody>
                {data.items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400" data-testid="credit-report-empty">Nenhuma venda a prazo no período.</td></tr>}
                {data.items.map((it) => (
                  <tr key={it.id} className="border-t border-slate-100" data-testid={`credit-report-row-${it.id}`}>
                    <td className="p-3">{fmtDate(it.created_at)}</td>
                    <td className="p-3 font-bold text-blue-700">{it.sale_number ? `#${it.sale_number}` : '—'}</td>
                    <td className="p-3">{it.customer_name}</td>
                    <td className="p-3 text-right font-bold">{money(it.amount)}</td>
                    <td className="p-3 text-right text-emerald-700">{money(it.amount_paid)}</td>
                    <td className="p-3 text-center"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${STATUS[it.status]?.cls}`}>{STATUS[it.status]?.label || it.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default CreditSalesReport;
